import {Injectable} from '@angular/core';
import 'rxjs/add/operator/map';
import * as firebase from 'firebase';
import {Doctor} from "../../models/doctor/doctor.interface";
import {BehaviorSubject, Observable, Subject} from "rxjs";
import {Center} from "../../models/center/center.interface";
import {Patient} from "../../models/patient/patient.interface";
import {forkJoin} from "rxjs/observable/forkJoin";
import {DatePipe} from '@angular/common'
import {Entry} from "../../models/entry/entry.interface";
import {Config} from "../../models/config/config.interface";
import {AngularFireDatabase} from "angularfire2/database";
import {from} from "rxjs/observable/from";
import {Regime} from "../../models/regime/regime.interface";
import {CenterShort} from "../../models/center/centerShort.interface";

@Injectable()
export class FirebaseProvider {

    private internetConnection = new BehaviorSubject<boolean>(false); // true is your initial value
    iConnection$ = this.internetConnection.asObservable();

    constructor(private database: AngularFireDatabase,
    ) {
        this.initFirebaseConnectionListener();
    }

    /**
     * Return a doctor subject for a given uid
     */
    getDoctorForUID(uid: string): Subject<Doctor> {
        let docSubject = new Subject<Doctor>();

        let doctorRef = firebase.database().ref('/doctors/' + uid);

        // fetch the doc
        let doc = {} as Doctor;
        doctorRef.once('value').then(function (doctorSnapshot) {
            if (!doctorSnapshot.exists()) {
                // no such doc
                docSubject.error('No such doctor with UID: ' + uid);
            } else {
                // create doc object
                let doctorVal = doctorSnapshot.val();
                doc.center = doctorVal.center;
                doc.displayName = doctorVal.displayName;

                docSubject.next(doc);
                docSubject.complete();
            }
        }, (e) => {
            console.log("Error while fetching doc data: " + JSON.stringify(e));
        });

        return docSubject;
    }


    /**
     * Returns a center fully populated with patients
     */
    getCenter(centerKey: string): Subject<Center> {
        let centerSubject = new Subject<Center>();

        let centerRef = firebase.database().ref('/centers/' + centerKey);

        // fetch the center
        let center = {} as Center;
        centerRef.once('value').then(function (centerSnapshot) {
            if (!centerSnapshot.exists()) {
                // no such center
                centerSubject.next(null);
                centerSubject.complete();
            } else {
                // create center object
                let centerVal = centerSnapshot.val();
                //console.log("center val: " + JSON.stringify(centerVal));

                center.name = centerVal.name;
                center.address = centerVal.address;
                center.patients = [];

                // build list of observables

                //console.log("Build observables: " + JSON.stringify(centerVal.patients));
                if (centerVal.patients) {
                    const patientListObservers = Object.getOwnPropertyNames(centerVal.patients).map(patientId =>
                        this.getPatient(patientId).first().timeout(10 * 1000)
                    );


                    // wait until we got all patients (all observables succeeded)
                    forkJoin(patientListObservers).first().timeout(30 * 1000).subscribe(function (patientList) {
                            // only use non null patients
                            center.patients = patientList.filter(p => p != null);
                            centerSubject.next(center);
                            centerSubject.complete();
                        }.bind(this), (error => {
                                console.log("Error: " + JSON.stringify(error));
                                centerSubject.next(null);
                                centerSubject.complete();
                            }
                        )
                    );
                } else {
                    centerSubject.next(center);
                    centerSubject.complete();

                }
            }
        }.bind(this));

        return centerSubject;
    }

    // get the center without the patients
    getCenterShort(centerKey: string): Subject<CenterShort> {
        let centerShortSubject = new Subject<CenterShort>();

        let centerRef = firebase.database().ref('/centers/' + centerKey);

        // fetch the center
        let center = {} as CenterShort;
        centerRef.once('value').then(function (centerSnapshot) {
            if (!centerSnapshot.exists()) {
                // no such center
                centerShortSubject.next(null);
                centerShortSubject.complete();
            } else {
                // create center object
                let centerVal = centerSnapshot.val();
                //console.log("center val: " + JSON.stringify(centerVal));

                center.name = centerVal.name;
                center.address = centerVal.address;
                center.kostenstelle = centerVal.kostenstelle;
                centerShortSubject.next(center);
                centerShortSubject.complete();
            }
        }.bind(this));

        return centerShortSubject;
    }


    /**
     * Get patient for uid
     */
    getPatient(patientUID): Subject<Patient> {
        let patientSubject = new Subject<Patient>();

        let configRef = firebase.database().ref('/config/' + patientUID);
        configRef.once('value').then(configSnapshot => {
            if (!configSnapshot.exists()) {
                // no such center
                patientSubject.next(null);
                patientSubject.complete();
            } else {

                // create patient object
                let configVal = configSnapshot.val();

                let patient = {} as Patient;

                patient.center = configVal.center;
                patient.patientId = patientUID;
                patient.nachname = configVal['nachname'];
                patient.vorname = configVal['vorname'];
                patient.geburtstagOrg = configVal['geburtstag']; // its a date object
                let birthDate = new DatePipe("de").transform(configVal['geburtstag'], 'dd.MM.yyyy');
                patient.geburtstag = birthDate;
                patient.fullname = patient.vorname + " " + patient.nachname;
                patient.dialysetyp = configVal.dialysetyp;
                patient.dialyseSeit = configVal.dialyseSeit;

                patientSubject.next(patient)

            }
        }, e => {
            console.log("Error: " + JSON.stringify(e));
            patientSubject.next(null);
            patientSubject.complete();
        });

        return patientSubject;
    }

    /**
     * Get list of entries for patient in one go and returns an observable of Entry[]
     * @param patientUID - the patient uid
     * @param count - the number of entries to be returned, sorted by sortierDatum
     */
    getEntries(patientUID, count = 2): Observable<Entry[]> {
        //console.log("in firebaseProvider.getEntries() for patient: " + patientUID);

        let entryRef = firebase.database().ref('/entry/' + patientUID);
        return from(entryRef.orderByChild("sortierDatum").once("value")).map(entriesSnapshot => {
                let entries: Entry[] = [];

                entriesSnapshot.forEach(entrySnapshot => {
                    // create entry object
                    let entry = entrySnapshot.val() as Entry;

                    entry['$key'] = entrySnapshot.key;
                    entry['$exists'] = entrySnapshot.exists();

                    entries.push(entry);

                    // fix entries - TODO - finish, move to somewhere else
                    entries.forEach(e => {
                        e.gewicht = e.gewicht * 1 || null;
                        e.groesse = e.groesse * 1 || null;
                        e.koerperTemperatur = e.koerperTemperatur * 1 || null;
                        e.pulsfrequenz = e.pulsfrequenz * 1 || null;
                        e.pulsfrequenz2 = e.pulsfrequenz2 * 1 || null;
                        e.pulsfrequenz3 = e.pulsfrequenz3 * 1 || null;
                        e.diastolischerBlutdruck = e.diastolischerBlutdruck * 1 || null;
                        e.diastolischerBlutdruck2 = e.diastolischerBlutdruck2 * 1 || null;
                        e.diastolischerBlutdruck3 = e.diastolischerBlutdruck3 * 1 || null;
                        e.systolischerBlutdruck = e.systolischerBlutdruck * 1 || null;
                        e.systolischerBlutdruck2 = e.systolischerBlutdruck2 * 1 || null;
                        e.systolischerBlutdruck3 = e.systolischerBlutdruck3 * 1 || null;
                        e.MAD = e.MAD * 1 || null;
                        e.MAD2 = e.MAD2 * 1 || null;
                        e.MAD3 = e.MAD3 * 1 || null;
                        e.ultrafiltration = e.ultrafiltration * 1 || null;

                        //console.log("ENTRY", e);
                    })

                    // we stop (return true) if we already found $count entries
                    return (entries.length > count);
                });

                // console.log(`getEntries(limit=${count}): Found ${entries.length} entries for patient: ${patientUID}`);

                return entries;
            }
        );
    }

    deleteEntry(patientUID, entry: Entry): Observable<void> {
        return from(this.database.object('/entry/' + patientUID + '/' + entry['$key']).remove());
    }


    private initFirebaseConnectionListener() {
        var connectedRef = firebase.database().ref(".info/connected");
        connectedRef.on("value", snap => {
            if (snap.val() === true) {
                this.internetConnection.next(true);
                console.log("internet connected");
            } else {
                this.internetConnection.next(false);
                console.log("internet not connected");
            }
        });
    }

    getPatientConfig(patientUid: string): Subject<Config> {
        let configSubject = new Subject<Config>();
        let configRef = firebase.database().ref('/config/' + patientUid);
        // fetch patient config
        configRef.once('value').then(function (configSnapshot) {
            if (!configSnapshot.exists()) {
                // no such doc
                configSubject.error('in firebaseProviders, method: getPatientConfig: No such patient config for UID: ' + patientUid);
            } else {
                configSubject.next(configSnapshot.val());
                configSubject.complete();
            }
        }, (e) => {
            console.error("Error while fetching config data: " + JSON.stringify(e));
        });

        return configSubject;
    }


    getCenters(): Subject<Center[]> {
        //console.log("in firebaseProviders.getCenters...");
        let centerListSubject = new Subject<Center[]>();

        let centers = [];

        let centerRef = firebase.database().ref('/centers/').orderByChild('name');
        centerRef.once("value", centerSnapshot => {
                centerSnapshot.forEach(childSnapshot => {
                    //console.log("GETTING CENTER--->" + JSON.stringify(childSnapshot.val()));
                    // create center object
                    let center = childSnapshot.val() as Center;
                    center['$key'] = childSnapshot.key;
                    center['$exists'] = childSnapshot.exists();

                    centers.push(center);
                    return false;
                });

                centerListSubject.next(centers);
                centerListSubject.complete();
            }, e => centerListSubject.complete()
        );

        return centerListSubject;
    }

    getPatients(): Subject<Patient[]> {
        //console.log("in firebaseProviders.getPatients....");
        let patientListSubject = new Subject<Patient[]>();

        let patients = [];

        let patientRef = firebase.database().ref('/config/').orderByChild('nachname');
        patientRef.once("value", patientSnapshot => {
                patientSnapshot.forEach(childSnapshot => {
                    // create the doctor object
                    //console.log("Doctor--->" + JSON.stringify(childSnapshot.val()));
                    let patient = childSnapshot.val() as Patient;
                    patient['$key'] = childSnapshot.key;
                    patient['$exists'] = childSnapshot.exists();

                    patients.push(patient);
                    return false
                });

                patientListSubject.next(patients);
                patientListSubject.complete();
            }, e => patientListSubject.complete()
        );

        return patientListSubject;
    }


    getDoctors(): Subject<Doctor[]> {
        //console.log("in firebaseProviders.getDoctors....");
        let doctorListSubject = new Subject<Doctor[]>();

        let doctors = [];

        let doctorRef = firebase.database().ref('/doctors/').orderByChild('displayName');
        doctorRef.once("value", doctorSnapshot => {
                doctorSnapshot.forEach(childSnapshot => {
                    // create the doctor object
                    //console.log("Doctor--->" + JSON.stringify(childSnapshot.val()));
                    let doctor = childSnapshot.val() as Doctor;
                    doctor['$key'] = childSnapshot.key;
                    doctor['$exists'] = childSnapshot.exists();

                    doctors.push(doctor);
                    return false
                });

                doctorListSubject.next(doctors);
                doctorListSubject.complete();
            }, e => doctorListSubject.complete()
        );

        return doctorListSubject;
    }

    updateCenter(center: Center): Observable<void> {
        let path = '/centers/' + center["$key"];
        //console.log("step 1-->in FB updateCenter path" + path);

        delete center["$key"];
        delete center["$exists"];
        //console.log("in FB updateCenter path" + path);
        return from(this.database.object(path).update(center));
    }

    deleteCenter(center: Center): Observable<void> {
        return from(this.database.object('/centers/' + center["$key"]).remove());
    }

    updateDoctor(doctor: Doctor): Observable<void> {
        let path = '/doctors/' + doctor["$key"];
        //debugger;
        delete doctor["$key"];
        delete doctor["$exists"];

        return from(this.database.object(path).update(doctor));
    }

    getPatientsForCenter(centerKey: string): Subject<Patient[]> {
        let patientListSubject = new Subject<Patient[]>();
        let patients = [];
        let pRef = firebase.database().ref('/config/').orderByChild("center").equalTo(centerKey);
        pRef.once("value", configs => {
                configs.forEach(config => {

                    let patient = config.val() as Patient;
                    //console.log("CONFIG found for "+ centerKey + "  ===>" + JSON.stringify(patient));
                    //console.log("CONFIG found for " + centerKey + "  ===>" + patient.vorname + " " + patient.nachname);

                    patients.push(patient);
                    return false;
                });
                patientListSubject.next();
                patientListSubject.complete();
            }, e => patientListSubject.complete()
        );

        return patientListSubject;
    }

    /**
     * return  all admin users key in a string array.
     */
    getAllAdminUsers(): Subject<string[]> {
        //console.log("in firebaseProviders.getAllAdmin....");
        let adminUserListSubject = new Subject<string[]>();

        let adminUsers = [];

        let adminUserRef = firebase.database().ref('/admin/');
        adminUserRef.once("value", allAdminUsersSnapshot => {
                allAdminUsersSnapshot.forEach(oneUserSnapshot => {
                    // create the adminUser object
                    //console.log("admin--->" + JSON.stringify(oneUserSnapshot.val()));
                    adminUsers.push(oneUserSnapshot.key);
                    return false;
                });
                adminUserListSubject.next(adminUsers);
                adminUserListSubject.complete();
            }, e => adminUserListSubject.complete()
        );

        return adminUserListSubject;
    }

    getPatientEntry(patientId: string, entryKey: string): Subject<Entry> {
        let entrySubject = new Subject<Entry>();
        let entryRef = firebase.database().ref('/entry/' + patientId + '/' + entryKey);
        // fetch patient config
        entryRef.once('value').then(function (entrySnapshot) {
            if (!entrySnapshot.exists()) {
                // no such doc
                entrySubject.error('in firebaseProviders, method: getEntryByKey: No such patient config for UID: ' + patientId);
            } else {
                let dataObj = entrySnapshot.val();
                dataObj['$key'] = entrySnapshot.key;
                dataObj['$exists'] = entrySnapshot.exists();
                entrySubject.next(dataObj);
                entrySubject.complete();
            }
        }, (e) => {
            console.error("Error while fetching config data: " + JSON.stringify(e));
        });

        return entrySubject;
    }

    getPatientEntryByCriteria(patientId: string, colName: string, colVal) {
        //console.log(`getPatientEntryByCriteria: patientId=${patientId}, colName=${colName},  colVal=${colVal}`);
        let entrySubject = new Subject<Entry>();
        let entryRef = firebase.database().ref('/entry/' + patientId);

        entryRef.orderByChild(colName).equalTo(colVal).once('value').then(function (entrySnapshot) {
            if (!entrySnapshot.exists()) {
                // no such doc
                entrySubject.error(`in firebaseProviders, method: getPatientEntryByCriteria: No data found for col:${colName}, value:${colVal}for patientId:${patientId} `);
            } else {
                //https://stackoverflow.com/questions/58822552/retrieving-data-from-firebase-in-angular-based-on-a-value
                //let iEntry = entrySnapshot[Object.keys(entrySnapshot)[0]]; //look at the above link for data retrieval
                //let iWeight = iEntry.gewicht;

                let dataObj = entrySnapshot.val();
                entrySubject.next(dataObj);
                entrySubject.complete();
            }
        }, (e) => {
            console.error("Error while fetching data in getPatientEntryByCriteria data: " + JSON.stringify(e));
        });
        return entrySubject;
    }

    // this method will return a list with given criteria
    getPatientEntriesByColumnValue(patientUID, columnName: string, colVal): Observable<Entry[]> {
        //https://stackoverflow.com/questions/41088059/query-firebase-with-angularfire2-query-getting-only-one-object
        //console.log(`in firebaseProvider.getEntryByColumnValue() for patient:${patientUID} AND refDateShortShort: ${refDateShortShort}`);
        let entryRef = firebase.database().ref('/entry/' + patientUID);
        return from(
            this.database.list(entryRef, {
                query: {
                    orderByChild: columnName,
                    equalTo: colVal,
                    limitToFirst: 1,
                }
            })
        );
    }

    updateEntry(patientId: string, entryObj: Entry): Observable<void> {
        let path = '/entry/' + patientId + "/" + entryObj["$key"];
        //console.log("in FB updateEntry path" + path);
        //console.log("in FB updateRegime edited regime: " + editedRegimeObj["name"] );
        delete entryObj["$key"];
        delete entryObj["$exists"];
        return from(this.database.object(path).update(entryObj));
    }

    addEntry(patientId, entryObj: Entry) {
        let path = '/entry/' + patientId;
        //debugger;
        delete entryObj["$key"];
        delete entryObj["$exists"];
        this.database.list(path).push(entryObj);
        //console.log("ADDED a new entry:" + entryObj);
    }

    updateConfig(patientId, config: Config): Observable<void> {
        let path = '/config/' + patientId + "/";
        //console.log("in FB updateCenter path" + path);

        delete config["$key"];
        delete config["$exists"];

        return from(this.database.object(path).update(config));
    }

    updateRegime(patientId, editedRegimeObj): Observable<void> {
        let path = '/config/' + patientId + "/regime/" + editedRegimeObj["$key"];
        //console.log("in FB updateRegime path" + path);
        //console.log("in FB updateRegime edited regime: " + editedRegimeObj["name"] );
        delete editedRegimeObj["$key"];
        delete editedRegimeObj["$exists"];
        return from(this.database.object(path).update(editedRegimeObj));
    }

    addRegime(patientId, regime: Regime) {
        let path = '/config/' + patientId + "/regime/";
        //debugger;
        delete regime["$key"];
        delete regime["$exists"];
        this.database.list(path).push(regime);
        //console.log("ADDED a new regime:" + regime);
    }

    addRegime2(patientId, regime: Regime):Observable<void> {
        let path = '/config/' + patientId + "/regime/";
        //debugger;
        delete regime["$key"];
        delete regime["$exists"];
        return from(this.database.list(path).push(regime));
    }

    // get all the regimes for a patient
    getRegimes(patientId): Subject<Regime[]> {
        let regimeListSubject = new Subject<Regime[]>();
        let regimes = [];
        let pathToPatientRegime = '/config/' + patientId + '/regime';
        let regimeRef = firebase.database().ref(pathToPatientRegime); //.orderByChild('name')

        regimeRef.once("value", regimeListSnapshot => {
                regimeListSnapshot.forEach(childSnapshot => {
                    //console.log("GETTING REGIME for a Patient--->" + JSON.stringify(childSnapshot.val()));
                    // create regime object
                    let regime = childSnapshot.val() as Regime;
                    regime['$key'] = childSnapshot.key;
                    regime['$exists'] = childSnapshot.exists();

                    regimes.push(regime);
                    return false;
                });

                regimeListSubject.next(regimes);
                regimeListSubject.complete();
            }, e => regimeListSubject.complete()
        );
        return regimeListSubject;
    }

    // get the regime by key and  patientid
    getRegimeByKey(patientId, regimeKey): Subject<Regime> {
        let regimeSubject = new Subject<Regime>();
        let pathToPatientRegime = '/config/' + patientId + '/regime/' + regimeKey;
        let regimeRef = firebase.database().ref(pathToPatientRegime);

        regimeRef.once('value').then(regimeSnapshot => {
            if (!regimeSnapshot.exists()) {
                // no such regime
                regimeSubject.error(`in firebaseProviders, method: getRegimeByKey: No such patient regime for regimeKey:${regimeKey} ` );
            } else {
                let dataObj = regimeSnapshot.val();
                dataObj['$key'] = regimeSnapshot.key;
                dataObj['$exists'] = regimeSnapshot.exists();
                regimeSubject.next(dataObj);
                regimeSubject.complete();
            }
        }, e => {
            console.error("Error while fetching Regime By Key : " + JSON.stringify(e));
        });

        return regimeSubject;
    }

    // check if a regime is in use by a patient entry
    checkIfRegimeInUse(patientId, regimeKey): Subject<Entry> {
        let entrySubject = new Subject<Entry>();
        let pathToPatientEntry = '/entry/' + patientId ;
        let iRef = firebase.database().ref(pathToPatientEntry);
        iRef.orderByChild("regime").equalTo(regimeKey).once("value", entrySnapshot =>{
            if (!entrySnapshot.exists()) {
                // no such regime
                entrySubject.error(`in firebaseProviders, method: getRegimeByKey: No such patient regime for regimeKey:${regimeKey} ` );
            } else {
                let dataObj = entrySnapshot.val();
                dataObj['$key'] = entrySnapshot.key;
                dataObj['$exists'] = entrySnapshot.exists();
                entrySubject.next(dataObj);
                entrySubject.complete();
            }
        }, e => {
            console.error("Error while fetching Regime By Key : " + JSON.stringify(e));
        });
        return entrySubject;
    }

    savePatientToCenter(patientId, centerList, center) {
        //console.log("In firebaseProvider.savePatientToCenter()");
        //go thru the center list to remove the patient from the old center
        centerList.forEach((center) => {
            //console.log("In savePatientToCenter() looping thru the center list to find the patient");
            this.database.object('/centers/' + center.centerKey + '/patients/' + patientId).remove();
        });

        // only add new entry if a center is picked
        if (center) {
            //now add the patient to the selected center
            this.database.object('/centers/' + center + '/patients/' + patientId).set(patientId);
            //console.log("patient is set to the selected center");
        }
    }

    getKostenstelleFromCenter(centerKey) {

    }
}



// WEBPACK FOOTER //
// ./src/providers/firebase/firebaseProvider.ts